import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import LmsAuthShell from "../components/LmsAuthShell";
import LmsFormField from "../components/LmsFormField";
import { loginLmsUser } from "../controllers/lmsAuthController";
import { resendVerification } from "../../../controller/email/emailController";
import { getCurrentUser } from "../../../controller/users/usersController";
import { logoutUser, setLmsCredObj } from "../../../slices/auth/authenticationSlice";
import errorHandler from "../../../utils/errorHandler";
import { buildLmsCredential } from "../utils/lmsSession";
import { getLmsRole } from "../utils/lmsRole";

const UNVERIFIED_EMAIL_LOGIN_ERROR =
  "Please verify your email before logging in as teacher or parent.";

const isUnverifiedEmailLoginError = (error) => {
  const message =
    (typeof error === "string" && error) ||
    error?.error ||
    error?.message ||
    error?.response?.data?.error ||
    "";

  return message === UNVERIFIED_EMAIL_LOGIN_ERROR;
};

const LmsLoginPage = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const navigateToVerification = (email, verificationCodeSent = false) => {
    const encodedEmail = encodeURIComponent(email || "");
    const sentQuery = verificationCodeSent ? "&sent=1" : "";

    navigate(`/lms/verify-email?email=${encodedEmail}&from=login${sentQuery}`, {
      replace: true,
      state: {
        fromLogin: true,
        email,
        verificationCodeSent,
      },
    });
  };

  const sendVerificationCode = async (email) => {
    try {
      await dispatch(resendVerification({ email })).unwrap();
      return true;
    } catch (err) {
      errorHandler(err);
      return false;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await dispatch(loginLmsUser(formData)).unwrap();

      // Replace any existing regular/LMS session before storing the new LMS token.
      dispatch(logoutUser());

      const loginCredential = buildLmsCredential(response);

      if (!loginCredential.token) {
        errorHandler("Login succeeded but no educator token was returned.");
        return;
      }

      dispatch(setLmsCredObj(loginCredential));

      const current = await dispatch(getCurrentUser()).unwrap();
      const currentUser = current?.data;
      const lmsRole = getLmsRole({ currentUser, credObj: response });
      const credential = buildLmsCredential(response, { lmsRole, user: currentUser });
      dispatch(setLmsCredObj(credential));

      if (currentUser?.isEmailVerified === false) {
        const resolvedEmail = currentUser.email || formData.email;
        const verificationCodeSent = await sendVerificationCode(resolvedEmail);
        navigateToVerification(resolvedEmail, verificationCodeSent);
        return;
      }

      toast.success("Welcome back");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      if (isUnverifiedEmailLoginError(error)) {
        const verificationCodeSent = await sendVerificationCode(formData.email);
        navigateToVerification(formData.email, verificationCodeSent);
        return;
      }

      errorHandler(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LmsAuthShell
      title="Login to ReadRivals"
      subtitle="Return to your reading-growth workspace and keep the momentum moving."
    >
      <form className="lms-form" onSubmit={handleSubmit}>
        <LmsFormField
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          autoComplete="email"
        />
        <LmsFormField
          label="Password"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          autoComplete="current-password"
        />
        <button className="lms-submit" type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Logging in..." : "Login"}
        </button>
        <p className="lms-auth-switch">
          New to ReadRivals LMS? <Link to="/lms/register">Create account</Link>
        </p>
      </form>
    </LmsAuthShell>
  );
};

export default LmsLoginPage;
